import React, { useRef, useCallback, useEffect, useMemo, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Box, Skeleton, IconButton } from "@mui/material";
import { getIcon } from "@dishopsaas/design-system";
import MapMarker from "./MapMarker";
import getStyles from "./MapMarker.styles";
import {
  recenterMap,
  zoomInMap,
  zoomOutMap,
  renderCenter,
  fitMapToBounds,
} from "./MapWithLatLng.services";
import { DARK, ICONS, LIGHT } from "../../constants";
import { Spinner } from "react-bootstrap";

const MapWithLatLng = ({
  isDelivery,
  lat,
  lng,
  apiKey,
  secondaryColor,
  zoom = 15,
  height = "180px",
  theme = "light",
  useCustomMarker = false,
  markerInfo,
  borderRadius = 1,
  mapBorderRadius = "8px",
  shopName = "",
  image = "",
  text = "",
  showOnlyMap = false,
  withIcons = false,
  isInteractive = true,
  driverLat,
  driverLng,
}: {
  isDelivery: boolean;
  lat: number;
  lng: number;
  apiKey: string;
  secondaryColor: string;
  zoom?: number;
  height?: string;
  theme?: string;
  useCustomMarker?: boolean;
  markerInfo?: {
    duration?: number;
    time?: moment.Moment;
  };
  borderRadius?: number;
  mapBorderRadius?: string;
  containerHeight?: string;
  shopName?: string;
  image?: string;
  text?: string;
  showOnlyMap?: boolean;
  withIcons?: boolean;
  isInteractive?: boolean;
  driverLat?: number;
  driverLng?: number;
}) => {

  const stableDriverLat = useMemo(() => driverLat, [driverLat]);
  const stableDriverLng = useMemo(() => driverLng, [driverLng]);
  const styles = getStyles(secondaryColor);
  const mapRef = useRef<google.maps.Map | null>(null);
  const isMountedRef = useRef(false);
  const [tilesLoaded, setTilesLoaded] = useState(false);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: apiKey,
  });
  const containerStyle = {
    width: "100%",
    height,
    borderRadius: mapBorderRadius,
    overflow: "hidden",
  };
  const mapStyles = [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "administrative",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ];
  const isDark = theme === DARK;
  const colorScheme = isDark ? DARK : LIGHT;
  const center = useMemo(() => {
    return stableDriverLat && stableDriverLng
      ? {
        lat: (lat + stableDriverLat) / 2,
        lng: (lng + stableDriverLng) / 2,
      }
      : renderCenter(useCustomMarker, lat, lng);
  }, [lat, lng, stableDriverLat, stableDriverLng, useCustomMarker]);
  const handleRecenter = useCallback(() => {
    recenterMap(
      mapRef.current,
      lat,
      lng,
      useCustomMarker,
      stableDriverLat,
      stableDriverLng,
    );
  }, [lat, lng, stableDriverLat, stableDriverLng, useCustomMarker]);

  const handleZoomIn = useCallback(() => {
    zoomInMap(mapRef.current, lat, lng, stableDriverLat, stableDriverLng);
  }, [lat, lng, stableDriverLat, stableDriverLng]);

  const handleZoomOut = useCallback(() => {
    zoomOutMap(mapRef.current, lat, lng, stableDriverLat, stableDriverLng);
  }, [lat, lng, stableDriverLat, stableDriverLng]);

  useEffect(() => {
    if (!mapRef.current || !stableDriverLat || !stableDriverLng) return;
    fitMapToBounds(mapRef.current, lat, lng, stableDriverLat, stableDriverLng);
  }, [stableDriverLat, stableDriverLng, lat, lng]);

  return (
    <Box
      sx={{
        borderRadius,
        overflow: "visible",
        position: "relative",
        height
      }}
    >
      {!tilesLoaded && (
        <Box sx={{ ...styles.spinner, bottom: withIcons ? '0px' : '168px' }}>
          <Spinner />
        </Box>
      )}
      <>
        <GoogleMap
          mapContainerStyle={{
            ...containerStyle,
            visibility: tilesLoaded ? "visible" : "hidden",
          }}
          center={center}
          zoom={zoom}
          onLoad={(map) => {
            mapRef.current = map;
            isMountedRef.current = true;
            if (stableDriverLat && stableDriverLng) {
              fitMapToBounds(map, lat, lng, stableDriverLat, stableDriverLng);
            }
            google.maps.event.addListenerOnce(map, "tilesloaded", () => {
              setTilesLoaded(true);
            });
          }}
          options={{
            disableDefaultUI: true,
            zoomControl: false,
            styles: mapStyles,
            colorScheme,
            ...(!isInteractive && {
              gestureHandling: "none",
              draggable: false,
              scrollwheel: false,
              disableDoubleClickZoom: true,
              keyboardShortcuts: false,
            }),
          }}
        >
          {tilesLoaded && (
            <>
              <MapMarker
                lat={lat}
                lng={lng}
                isDelivery={isDelivery}
                secondaryColor={secondaryColor}
                useCustomMarker={useCustomMarker}
                markerInfo={markerInfo}
                shopName={shopName}
                image={image}
                text={text}
                showOnlyMap={showOnlyMap}
              />
              {stableDriverLat && stableDriverLng && (
                <MapMarker
                  lat={stableDriverLat}
                  lng={stableDriverLng}
                  isDelivery={true}
                  secondaryColor={secondaryColor}
                  useCustomMarker={false}
                  showOnlyMap={true}
                  isDriver={true}
                />
              )}
            </>)}
        </GoogleMap>

        {withIcons && (
          <Box sx={styles.controls}>
            <IconButton onClick={handleRecenter}>
              {getIcon(ICONS.Locate, { size: 20 })}
            </IconButton>
            <IconButton onClick={handleZoomIn}>
              {getIcon(ICONS.Plus, { size: 20 })}
            </IconButton>
            <IconButton onClick={handleZoomOut}>
              {getIcon(ICONS.Minus, { size: 20 })}
            </IconButton>
          </Box>
        )}
      </>

    </Box>
  );
};

export default React.memo(MapWithLatLng);
